import { supabase } from '../../config/supabase';

const PAGE_SIZE = 25;

/**
 * Fetch completed delivery rows, paginated, one card per job_deliveries row.
 *
 * Queries job_deliveries (the authoritative source for delivery completion)
 * rather than job_quotes, so both old-system and new-system (child_unique_id)
 * deliveries appear as soon as the driver marks them delivered.
 *
 * Supports both systems:
 *   New system – delivery.child_unique_id === job_quotes.id  (UUID match)
 *   Old system – match by quote_type + delivery_count
 *
 * @param {string}  companyId
 * @param {number}  page        - 0-based page index
 * @param {string}  [search]    - optional search string (customer name / address)
 * @param {string}  [quoteType] - optional filter: 'roof'|'wall'|'floor'|'timber'
 * @returns {{ data: Array, hasMore: boolean, total: number }}
 */
export const getFinishedDeliveries = async (
    companyId,
    page = 0,
    search = '',
    quoteType = ''
) => {
    try {
        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        let query = supabase
            .from('job_deliveries')
            .select(
                `
                id,
                quote_type,
                delivery_status,
                assigned_driver_id,
                assigned_driver_name,
                delivered_at,
                delivery_count,
                child_unique_id,
                jobs!inner (
                    id,
                    unique_id,
                    short_id,
                    customer_name,
                    customer_address,
                    job_type,
                    quote_sent,
                    invoiced,
                    company_id,
                    job_quotes (
                        id,
                        quote_type,
                        invoiced,
                        delivery_count,
                        expected_delivery_date,
                        child_unique_id
                    )
                )
                `,
                { count: 'exact' }
            )
            .eq('delivery_status', 'delivered')
            .eq('jobs.company_id', companyId)
            .eq('jobs.deleted', false)
            .order('delivered_at', { ascending: false });

        if (quoteType) {
            query = query.eq('quote_type', quoteType);
        }

        if (search && search.trim()) {
            query = query.or(
                `customer_name.ilike.%${search.trim()}%,customer_address.ilike.%${search.trim()}%`,
                { foreignTable: 'jobs' }
            );
        }

        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;

        // Flatten: attach job fields and find the matching quote row
        const rows = (data || []).map((row) => {
            const job = row.jobs;

            // Detect system type and find the matching job_quotes row
            const isNewSystem = !!row.child_unique_id;
            let quote = null;

            if (isNewSystem) {
                // New system: delivery.child_unique_id is the UUID of the job_quotes row
                quote = (job.job_quotes || []).find(q => q.id === row.child_unique_id) || null;
            } else {
                // Old system: match by quote_type + delivery_count
                const deliveryCount = row.delivery_count ?? 0;
                quote = (job.job_quotes || []).find(q =>
                    q.quote_type === row.quote_type &&
                    (q.delivery_count ?? 0) === deliveryCount
                ) || null;
            }

            console.log(row)

            return {
                id: row.id,                                      // job_deliveries.id (stable UUID)
                quoteId: quote?.id || null,
                jobId: job.id,
                uniqueId: job.unique_id,
                shortId: job.short_id,
                customerName: job.customer_name,
                customerAddress: job.customer_address,
                jobType: job.job_type,
                quoteType: row.quote_type,
                childUniqueId: row?.child_unique_id || null,
                quoteSent: job.quote_sent,
                invoiced: job.invoiced,
                quoteInvoiced: quote?.invoiced || false,
                deliveryStatus: row.delivery_status,
                expectedDeliveryDate: quote?.expected_delivery_date || null,
                deliveryCount: row.delivery_count,
                assignedDriverId: row.assigned_driver_id || null,
                assignedDriverName: row.assigned_driver_name || null,
                deliveredAt: row.delivered_at || null,
            };
        });

        return {
            data: rows,
            hasMore: count > to + 1,
            total: count || 0,
        };
    } catch (error) {
        console.error('Error fetching finished deliveries:', error);
        throw error;
    }
};

export const FINISHED_DELIVERIES_PAGE_SIZE = PAGE_SIZE;
